import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import { useNavigate } from "react-router-dom";
import "../assets/css/style.css";
import Modal from "../components/ui/Modal";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import UserAdd from "components/user/UserAdd";
import { useAddAdminMutation, useDeleteAdminMutation, useGetAdminQuery, useUpdateAdminMutation } from "apis/user";
import { useResetAdminMfaMutation } from "apis/auth";
import Table from "components/ui/Table";
import { FiShield } from "react-icons/fi";

const Admin = () => {
  const pageSize = 10;
  const navigate = useNavigate();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const {
    data,
    isPending: isPromptsPending,
  } = useGetAdminQuery();

  const {
    mutate: addAdmin,
  } = useAddAdminMutation();

  const {
    mutate: deleteAdmin,
  } = useDeleteAdminMutation();

  const {
    mutate: updateAdmin,
  } = useUpdateAdminMutation();

  const {
    mutate: resetAdminMfa,
  } = useResetAdminMfaMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
      },
      {
        accessorKey: "email",
        header: "Email",
        isFilter: true,
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: (row) => (
          <div>
            {row.role === "superAdmin" ? "Super Admin" : row.role === "beleefAdmin" ? "Beleef Admin" : row.role === "meloAdmin" ? "Melo Admin" : "Super Admin"}
          </div>
        ),
        isFilter: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => (
          <div>
            {row.status === "active"
              ? "Active"
              : row.status === "inactive"
              ? "Inactive"
              : "Pending"}
          </div>
        ),
        isFilter: true,
      },
      {
        accessorKey: "twoFactorEnabled",
        header: "MFA",
        cell: (row) => (
          <div>{row.twoFactorEnabled ? "Enabled" : "Not set up"}</div>
        ),
        isFilter: true,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: (row) => (
          <div>
            {new Date(row.createdAt).toLocaleDateString()}
          </div>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Admin"
            >
              <FiEdit2 fontSize={20} />
            </button>
            {/* The way back from a lost phone. Self-service removal is
                deliberately not possible, so this is the only route. */}
            {row.twoFactorEnabled && (
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      `Reset two-factor for ${row.email}? They'll be signed out and asked to set it up again at next sign-in.`,
                    )
                  ) {
                    resetAdminMfa(row._id);
                  }
                }}
                className="edit-icon"
                title="Reset MFA"
              >
                <FiShield fontSize={20} />
              </button>
            )}
            <button
              onClick={() => {
                setSelectedId(row._id);
                deleteAdmin(row._id);
              }}
              className="delete-icon"
              title="Delete Admin"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "160px",
      },
    ],
    [navigate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (isPromptsPending) return <PageLoader />;

  const AddAdminButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
    >
      <FaPlus size={16} className="mr-2" />
      Add Admin
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Admin"
        className="max-w-2xl"
      >
        <UserAdd
          initialName={data?.find((d) => d._id === selectedId)?.name}
          initialEmail={data?.find((d) => d._id === selectedId)?.email}
          initialRole={data?.find((d) => d._id === selectedId)?.role}
          onSubmit={(formData) => {
            updateAdmin({
              ...formData,
              id: selectedId,
            });
            setIsEditOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Create Admin"
        className="max-w-2xl"
      >
        <UserAdd
          initialName=""
          initialEmail=""
          initialRole=""
          onSubmit={(formData) => {
            addAdmin(formData);
            setIsAddOpen(false);
          }}
        />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Admins"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddAdminButton}
        />
      </div>
    </>
  );
};

export default Admin;
